import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { tokens } from "../../theme";
import apiService from "../../service/Api";
import { setReferralAuth } from "../../store/slice/ReferralAuthSlice";
import { login } from "../../store/slice/AuthSlice";
import { useDispatch } from "react-redux";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ReferralLoginForm({ open, onClose }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    username?: string;
    password?: string;
  }>({});

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[700],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!username.trim()) newErrors.username = "Email or username is required";
    if (!password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const dispatch = useDispatch();

  const handleSubmit = async () => {
    if (!validate() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await apiService.post<{
        access_token: string;
        refresh_token: string;
        user: {
          id: number;
          username: string;
          email: string;
          first_name: string;
          last_name: string;
          is_supper_user: boolean;
          permissions: string[];
          referral_partner: number;
        };
        message: string;
        is_2fa_enabled: boolean;
      }>(
        "/api/refferal-program/login/",
        { username: username.trim(), password },
        true,
      );

      // Store tokens

      localStorage.setItem("referral_access_token", response.data.access_token);
      localStorage.setItem(
        "referral_refresh_token",
        response.data.refresh_token,
      );
      localStorage.setItem("referral_user", JSON.stringify(response.data.user));

      dispatch(
        setReferralAuth({
          user: response.data.user,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
        }),
      );

      // ✅ Makes apiService interceptor pick up the referral token
      dispatch(
        login({
          user: {
            username: response.data.user.username,
            first_name: response.data.user.first_name,
            last_name: response.data.user.last_name,
            is_supper_user: "",
            permissions: response.data.user.permissions,
          },
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          is_2fa_enabled: response.data.is_2fa_enabled,
        }),
      );

      toast.success("Welcome back!");
      onClose();
      navigate(`/referral-dashboard/${response.data.user.referral_partner}`);
    } catch {
      toast.error("Invalid credentials. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "20px", overflow: "hidden" },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 4, px: 4 }}>
        <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
          Partner Login
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          mb={3}
        >
          Sign in to your referral partner account
        </Typography>

        <Box display="flex" flexDirection="column" gap={2.5}>
          {/* Username / Email */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Email or Username
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter your email or username"
              value={username}
              disabled={isSubmitting}
              onChange={(e) => {
                setUsername(e.target.value);
                if (errors.username)
                  setErrors((p) => ({ ...p, username: undefined }));
              }}
              error={!!errors.username}
              helperText={errors.username}
              sx={fieldSx}
            />
          </Box>

          {/* Password */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              disabled={isSubmitting}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password)
                  setErrors((p) => ({ ...p, password: undefined }));
              }}
              error={!!errors.password}
              helperText={errors.password}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitting}
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#724E41",
                py: 1.3,
                borderRadius: "24px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
                minWidth: "160px",
                "&:hover": { backgroundColor: "#4E342E" },
                "&.Mui-disabled": { backgroundColor: "#c4a99e", color: "#fff" },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Login"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
